import { useState, useRef } from 'react';
import Field from './Field';
import api from '../services/api';

/**
 * WishFormModal — the create/edit wish form, shared by the group Wishes tab
 * and the global My Wishes page (mirrors iOS WishFormView).
 *
 * Props:
 *  - editingId:     wish id when editing, null when creating
 *  - initialForm:   starting form values (use BLANK_WISH / wishToForm)
 *  - myGroups:      all the user's groups (multi-group share picker)
 *  - categories:    wish categories for the picker
 *  - events:        candidate events for the optional event picker (pass [] to hide)
 *  - lockedGroupId: a group that must stay selected (the group page passes its
 *                   own id; My Wishes passes null and requires ≥ 1 selection)
 *  - pets:          pets the caretaker owns that this wish could be FOR (pass []
 *                   to hide the picker). Selecting one attributes the wish to the
 *                   pet (sends `pet_id`); the wish still belongs to the caretaker.
 *  - lockedPetId:   a pet the wish is fixed to (the pet profile passes its own
 *                   id so "Add a wish" there always targets that pet)
 *  - onClose / onSaved / notify
 */

export const BLANK_WISH = {
    // What kind of wish this is (FEEDBACK_PLAN.md §7). A charity wish is a
    // "donate to X" instead of a physical item; it reuses url (donation link),
    // price_min/max (suggested amount) and description (note), and additionally
    // names the organization. `organization_name` is REQUIRED when type ===
    // 'charity' and ignored otherwise.
    type: 'item',
    organization_name: '',
    title: '',
    description: '',
    url: '',
    image_url: '',
    price_min: '',
    price_max: '',
    priority: 'medium',
    category_id: '',
    quantity_desired: 1,
    multiple_ok: false,
    is_generic: false,
    event_id: '',
    pet_id: '',
    child_id: '',
    group_ids: [],
    // Gift shares (FEEDBACK_PLAN.md §6): let several givers chip in on one wish,
    // coordinated by whoever claims it. `share_enabled` is a UI-only toggle; the
    // three fields below it are what actually go to the API.
    share_enabled: false,
    share_count: '',
    estimated_price: '',
    allow_partial_funding: false,
};

/** Map an API wish (full detail, incl. groups) into form values. */
export function wishToForm(w) {
    return {
        type: w.type || 'item',
        organization_name: w.organization_name || '',
        title: w.title || '',
        description: w.description || '',
        url: w.url || '',
        image_url: w.image_url || '',
        price_min: w.price_min ?? '',
        price_max: w.price_max ?? '',
        priority: w.priority || 'medium',
        category_id: w.category_id ?? '',
        quantity_desired: w.quantity_desired ?? 1,
        multiple_ok: !!w.multiple_ok,
        is_generic: !!w.is_generic,
        event_id: w.event_id ?? '',
        pet_id: w.pet_id ?? w.pet?.id ?? '',
        child_id: w.child_id ?? w.child?.id ?? '',
        group_ids: (w.groups || []).map((g) => g.id),
        // Any of the three shareable fields being set means chip-in is on.
        share_enabled: w.share_count != null || w.estimated_price != null || !!w.allow_partial_funding,
        share_count: w.share_count ?? '',
        estimated_price: w.estimated_price ?? '',
        allow_partial_funding: !!w.allow_partial_funding,
    };
}

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

export default function WishFormModal({
    editingId,
    initialForm,
    myGroups,
    categories,
    events = [],
    lockedGroupId = null,
    pets = [],
    childrenList = [],
    lockedChildId = null,
    lockedPetId = null,
    onClose,
    onSaved,
    notify,
}) {
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);

    // Auto-fetch a product image/title from a pasted URL (Open Graph tags).
    const [fetchingMeta, setFetchingMeta] = useState(false);
    const [metaNote, setMetaNote] = useState(null); // { warn, text }
    const lastFetchedUrl = useRef(initialForm.url || ''); // don't re-fetch an existing link

    const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

    const toggleGroup = (gid) => {
        setForm((f) => {
            const has = f.group_ids.includes(gid);
            return { ...f, group_ids: has ? f.group_ids.filter((g) => g !== gid) : [...f.group_ids, gid] };
        });
    };

    const fetchMeta = async (url) => {
        const u = (url || '').trim();
        if (!/^https?:\/\/.+/i.test(u) || u === lastFetchedUrl.current) return;
        lastFetchedUrl.current = u;
        setFetchingMeta(true);
        setMetaNote(null);
        try {
            const res = await api.get('/url-metadata', { params: { url: u } });
            setForm((f) => ({
                ...f,
                image_url: res.data.image || f.image_url,
                // Only fill the title if the user hasn't typed one.
                title: f.title.trim() ? f.title : res.data.title || f.title,
            }));
            if (!res.data.image) {
                // The site returned only a logo/placeholder (Amazon does this) or
                // no image at all — tell the user to paste one.
                setMetaNote({ warn: true, text: "Couldn't find a product image for that link — paste one below." });
            }
        } catch (err) {
            setMetaNote({ warn: true, text: "Couldn't read that link — you can paste an image URL below." });
        } finally {
            setFetchingMeta(false);
        }
    };

    const futureEvents = events.filter((e) => new Date(e.event_date) >= new Date(new Date().toDateString()));

    // A charity wish is a "donate to X" wish: it reveals a required organization
    // name and relabels the shared URL/price fields.
    const isCharity = form.type === 'charity';
    const WISH_TYPES = [
        { value: 'item', label: 'Item' },
        { value: 'experience', label: 'Experience' },
        { value: 'charity', label: 'Charity' },
    ];

    // Build the payload the API accepts. Empty strings become null / omitted.
    const buildPayload = () => ({
        // Wish kind. `organization_name` is only meaningful for a charity wish;
        // for other types we send null so switching a wish away from charity
        // clears any earlier org name.
        type: form.type || 'item',
        organization_name: form.type === 'charity' ? (form.organization_name || null) : null,
        title: form.title,
        description: form.description || null,
        url: form.url || null,
        image_url: form.image_url || null,
        price_min: form.price_min === '' ? null : form.price_min,
        price_max: form.price_max === '' ? null : form.price_max,
        priority: form.priority || null,
        category_id: form.category_id === '' ? null : form.category_id,
        quantity_desired: form.quantity_desired || 1,
        multiple_ok: form.multiple_ok,
        is_generic: form.is_generic,
        event_id: form.event_id === '' ? null : form.event_id,
        // Attribute the wish to a pet the caretaker owns, if one is chosen. A
        // locked pet (from the pet profile) always wins.
        pet_id: lockedPetId ?? (form.pet_id === '' ? null : Number(form.pet_id)),
        // A wish is for exactly one of: me, a pet, a child. The single recipient select
        // below makes that structurally true, and the API refuses both at once anyway
        // (`prohibits:pet_id`).
        child_id: lockedChildId ?? (form.child_id === '' ? null : Number(form.child_id)),
        // Gift shares. When the toggle is off we send nulls/false so turning it
        // off on an edit clears any earlier settings.
        share_count: form.share_enabled && form.share_count !== '' ? Number(form.share_count) : null,
        estimated_price: form.share_enabled && form.estimated_price !== '' ? form.estimated_price : null,
        allow_partial_funding: form.share_enabled ? !!form.allow_partial_funding : false,
    });

    const syncGroups = async (wishId, desiredIds, currentIds) => {
        const toAttach = desiredIds.filter((id) => !currentIds.includes(id));
        const toDetach = currentIds.filter((id) => !desiredIds.includes(id));
        if (toAttach.length) {
            await api.post(`/wishes/${wishId}/groups`, { group_ids: toAttach });
        }
        for (const gid of toDetach) {
            await api.delete(`/wishes/${wishId}/groups/${gid}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Where must the wish (still) be shared? The locked group always stays.
        const desiredGroups = Array.from(new Set([...(lockedGroupId ? [lockedGroupId] : []), ...form.group_ids]));
        if (desiredGroups.length === 0) {
            notify('error', 'Pick at least one group to share the wish into.');
            return;
        }

        // A charity wish must name the organization (the API also enforces this
        // with a 422; gating here is a friendlier failure).
        if (form.type === 'charity' && !form.organization_name.trim()) {
            notify('error', 'Add the organization name for this charity wish.');
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                await api.put(`/wishes/${editingId}`, buildPayload());
                const detail = await api.get(`/wishes/${editingId}`);
                const currentIds = (detail.data.groups || []).map((g) => g.id);
                await syncGroups(editingId, desiredGroups, currentIds);
                notify('success', 'Wish updated.');
            } else {
                // Create in the primary group, then attach any extra groups.
                const primary = lockedGroupId ?? desiredGroups[0];
                const res = await api.post('/wishes', { group_id: primary, ...buildPayload() });
                const extra = desiredGroups.filter((id) => id !== primary);
                if (extra.length) {
                    await api.post(`/wishes/${res.data.id}/groups`, { group_ids: extra });
                }
                notify('success', 'Wish added.');
            }
            onSaved();
        } catch (err) {
            notify('error', errText(err, 'Could not save the wish.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full my-8">
                <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Wish' : 'Add Wish'}</h3>
                <form onSubmit={handleSubmit} className="space-y-3">
                    {/* Wish type (FEEDBACK_PLAN.md §7) — Item / Experience /
                        Charity. Charity reveals the organization field below. */}
                    <Field label="Type">
                        <div className="flex gap-2">
                            {WISH_TYPES.map((t) => {
                                const active = form.type === t.value;
                                return (
                                    <button
                                        key={t.value}
                                        type="button"
                                        onClick={() => setField('type', t.value)}
                                        className={`flex-1 px-3 py-2 text-sm rounded-md border ${
                                            active
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>

                    {isCharity && (
                        <Field label="Organization name" required>
                            <input
                                type="text"
                                required
                                value={form.organization_name}
                                onChange={(e) => setField('organization_name', e.target.value)}
                                className={inputCls}
                                placeholder="e.g., Doctors Without Borders"
                            />
                        </Field>
                    )}

                    <Field label="Title" required>
                        <input
                            type="text"
                            required
                            value={form.title}
                            onChange={(e) => setField('title', e.target.value)}
                            className={inputCls}
                            placeholder="e.g., Wireless headphones"
                        />
                    </Field>

                    {/* Who is this wish for? ONE select covering myself / a pet I look
                        after / a child I'm a carer for. One control rather than two
                        because those are mutually exclusive — a wish has a single display
                        owner, and the API rejects both ids together. Only shown when
                        there is actually a choice to make (or a profile locked one in). */}
                    {lockedPetId || lockedChildId ? (
                        <Field label="This wish is for">
                            <div className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 flex items-center gap-2">
                                <span aria-hidden="true">{lockedChildId ? '🧒' : '🐾'}</span>
                                {lockedChildId
                                    ? (childrenList.find((c) => c.id === lockedChildId)?.name || 'This child')
                                    : (pets.find((p) => p.id === lockedPetId)?.name || 'This pet')}
                            </div>
                        </Field>
                    ) : pets.length > 0 || childrenList.length > 0 ? (
                        <Field label="This wish is for">
                            <select
                                value={form.child_id ? `child:${form.child_id}` : form.pet_id ? `pet:${form.pet_id}` : ''}
                                onChange={(e) => {
                                    const [kind, id] = e.target.value.split(':');
                                    setField('pet_id', kind === 'pet' ? id : '');
                                    setField('child_id', kind === 'child' ? id : '');
                                }}
                                className={inputCls}
                            >
                                <option value="">Myself</option>
                                {childrenList.map((c) => (
                                    <option key={`child-${c.id}`} value={`child:${c.id}`}>
                                        🧒 {c.name}
                                    </option>
                                ))}
                                {pets.map((p) => (
                                    <option key={`pet-${p.id}`} value={`pet:${p.id}`}>
                                        🐾 {p.name}{p.species ? ` (${p.species})` : ''}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    ) : null}

                    <Field label="Description">
                        <textarea
                            value={form.description}
                            onChange={(e) => setField('description', e.target.value)}
                            className={inputCls}
                            rows={2}
                        />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label={isCharity ? 'Donation link' : 'Product URL'}>
                            <input
                                type="url"
                                value={form.url}
                                onChange={(e) => setField('url', e.target.value)}
                                onBlur={(e) => fetchMeta(e.target.value)}
                                onPaste={(e) => {
                                    const text = e.clipboardData.getData('text');
                                    setTimeout(() => fetchMeta(text), 0);
                                }}
                                className={inputCls}
                                placeholder="https://…"
                            />
                            <p className={`text-xs mt-1 ${metaNote?.warn ? 'text-amber-600' : 'text-gray-400'}`}>
                                {fetchingMeta
                                    ? 'Fetching image from link…'
                                    : metaNote?.text || 'Paste a product link to auto-fill the image.'}
                            </p>
                        </Field>
                        <Field label="Image URL">
                            <input type="url" value={form.image_url} onChange={(e) => setField('image_url', e.target.value)} className={inputCls} placeholder="https://…" />
                            {form.image_url && (
                                <img src={form.image_url} alt="" className="mt-2 h-16 w-16 object-cover rounded border border-gray-200" />
                            )}
                        </Field>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <Field label={isCharity ? 'Suggested amount (min)' : 'Min price'}>
                            <input type="number" min="0" step="0.01" value={form.price_min} onChange={(e) => setField('price_min', e.target.value)} className={inputCls} />
                        </Field>
                        <Field label={isCharity ? 'Suggested amount (max)' : 'Max price'}>
                            <input type="number" min="0" step="0.01" value={form.price_max} onChange={(e) => setField('price_max', e.target.value)} className={inputCls} />
                        </Field>
                        <Field label="Quantity">
                            <input type="number" min="1" value={form.quantity_desired} onChange={(e) => setField('quantity_desired', e.target.value)} className={inputCls} />
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Priority">
                            <select value={form.priority} onChange={(e) => setField('priority', e.target.value)} className={inputCls}>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </Field>
                        <Field label="Category">
                            <select value={form.category_id} onChange={(e) => setField('category_id', e.target.value)} className={inputCls}>
                                <option value="">— none —</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.icon ? `${c.icon} ` : ''}{c.name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    {futureEvents.length > 0 && (
                        <Field label="Attach to event (optional)">
                            <select value={form.event_id} onChange={(e) => setField('event_id', e.target.value)} className={inputCls}>
                                <option value="">— none —</option>
                                {futureEvents.map((ev) => (
                                    <option key={ev.id} value={ev.id}>{ev.name}</option>
                                ))}
                            </select>
                        </Field>
                    )}

                    <div className="flex gap-6">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" checked={form.multiple_ok} onChange={(e) => setField('multiple_ok', e.target.checked)} />
                            Multiple OK (can be bought more than once)
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" checked={form.is_generic} onChange={(e) => setField('is_generic', e.target.checked)} />
                            Generic idea
                        </label>
                    </div>

                    {/* Gift shares — let several givers chip in on one expensive
                        wish. Whoever claims it coordinates; others pledge pieces
                        and/or a dollar amount. You (the wisher) never see who
                        chipped in — it stays a surprise. */}
                    <div className="rounded-md border border-gray-200 p-3 space-y-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                                type="checkbox"
                                checked={form.share_enabled}
                                onChange={(e) => setField('share_enabled', e.target.checked)}
                            />
                            Let people chip in on this gift
                        </label>
                        {form.share_enabled && (
                            <>
                                <p className="text-xs text-gray-500">
                                    Givers pledge toward this together, coordinated by whoever claims it.
                                    You won't see who pitched in.
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Number of shares">
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={form.share_count}
                                            onChange={(e) => setField('share_count', e.target.value)}
                                            className={inputCls}
                                            placeholder="e.g., 4"
                                        />
                                    </Field>
                                    <Field label="Estimated price">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={form.estimated_price}
                                            onChange={(e) => setField('estimated_price', e.target.value)}
                                            className={inputCls}
                                            placeholder="e.g., 100"
                                        />
                                    </Field>
                                </div>
                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={form.allow_partial_funding}
                                        onChange={(e) => setField('allow_partial_funding', e.target.checked)}
                                    />
                                    Allow partial funding (any amount is welcome)
                                </label>
                            </>
                        )}
                    </div>

                    <Field label="Share into groups" required={!lockedGroupId}>
                        <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                            {myGroups.map((g) => {
                                const isLocked = g.id === lockedGroupId;
                                const checked = isLocked || form.group_ids.includes(g.id);
                                return (
                                    <label key={g.id} className="flex items-center gap-2 text-sm text-gray-700">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={isLocked}
                                            onChange={() => toggleGroup(g.id)}
                                        />
                                        {g.name}
                                        {isLocked && <span className="text-xs text-gray-400">(this group)</span>}
                                    </label>
                                );
                            })}
                        </div>
                    </Field>

                    <div className="flex gap-2 justify-end pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Wish'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

